<template>
  <div class="itinerary-guide">
    <p class="itinerary-guide__intro">Edit dates directly on existing days, or insert a new day without recreating the itinerary.</p>

    <p class="itinerary-guide__operations">Confirmed or verified itineraries are locked. Contact Operations or a supervisor to reopen the itinerary before requesting changes. A pending amendment also cannot be accepted while the itinerary is locked.</p>

    <section class="itinerary-guide__section">
      <h3>Insert a day between two days</h3>
      <div class="itinerary-guide__cases">
        <article class="itinerary-guide__case">
          <span class="itinerary-guide__number">A</span>
          <div>
            <h4>Choose the dates yourself</h4>
            <p>Move the later day forward with the pencil, then click <strong>+ Before</strong> on that day. For example, change Day 2 from 06 Nov to 07 Nov, then insert before it. The new day gets 06 Nov.</p>
          </div>
        </article>
        <article class="itinerary-guide__case">
          <span class="itinerary-guide__number">B</span>
          <div>
            <h4>Let the dates move automatically</h4>
            <p>Click <strong>+ Before</strong> on Day 2 without changing its date. The new day takes 06 Nov; the original Day 2 moves to 07 Nov. Every later day also moves one calendar day forward. Review the new dates before saving or requesting.</p>
          </div>
        </article>
      </div>
    </section>

    <section class="itinerary-guide__section">
      <h3>Other positions</h3>
      <p><strong>+ Before</strong> on Day 1 adds a day with the preceding date. <strong>Add at end</strong> adds a day after the last date.</p>
    </section>

    <section class="itinerary-guide__section">
      <h3>Card guide</h3>
      <div class="itinerary-guide__legend">
        <span class="itinerary-guide__swatch itinerary-guide__swatch--existing" />
        <span>Existing day</span>
        <span class="itinerary-guide__swatch itinerary-guide__swatch--new" />
        <span>New day</span>
      </div>
      <div class="itinerary-guide__actions">
        <div><strong>+ Before</strong><span>Insert before this card</span></div>
        <div>
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M11.8 5.2 14.8 8.2M3.5 16.5l3.8-.8L16 7a1.7 1.7 0 0 0-2.4-2.4l-8.7 8.7-.8 3.2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
          <span>Edit the date, type, park or area</span>
        </div>
        <div>
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m4 10 4 4 8-8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          <span>Close the editing fields</span>
        </div>
        <div>
          <svg class="itinerary-guide__remove-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4.5 6h11M8 6V4.5h4V6m-6.5 0 .6 10h7.8l.6-10M8 9v4m4-4v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
          <span>Remove the day</span>
        </div>
      </div>
      <div class="itinerary-guide__operations">
        <strong>What happens to driver instructions</strong>
        <ul>
          <li>Date only: notes, crater transit and timings stay with the same day, including when Before shifts its date.</li>
          <li>New day: timings are created from the applicable template. Without a template, timings stay empty.</li>
          <li>Changed type, national park or area: existing notes, crater transit and timings are cleared; timings are rebuilt from the applicable template.</li>
          <li>Removed day: its notes and timings are deleted.</li>
        </ul>
        <p>These changes take effect when you save the itinerary or when the amendment is accepted.</p>
        <p v-if="showOperations">Current operational details are read only here.</p>
      </div>
      <p class="itinerary-guide__operations">Changing a day's date, type, park or area, or adding or removing days, reopens the driver itinerary when saved or when the amendment is accepted. Review the notes, timings and related bookings, then confirm and verify the itinerary again.</p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SafariItineraryHelp',
  props: {
    showOperations: { type: Boolean, default: false },
  },
}
</script>

<style scoped>
.itinerary-guide { color: #252525; font-size: 14px; line-height: 1.5; }
.itinerary-guide__intro { margin: 0 0 20px; color: #666; }
.itinerary-guide__section + .itinerary-guide__section { margin-top: 22px; padding-top: 20px; border-top: 1px solid #e7e7e7; }
.itinerary-guide__section h3 { margin: 0 0 12px; font-size: 16px; }
.itinerary-guide__section p { margin: 0; }
.itinerary-guide__cases { display: grid; gap: 12px; }
.itinerary-guide__case { display: flex; align-items: flex-start; gap: 12px; padding: 13px 15px; border: 1px solid #e1e6dc; border-radius: 9px; background: #fbfdf8; }
.itinerary-guide__number { display: inline-flex; flex: none; align-items: center; justify-content: center; width: 25px; height: 25px; border-radius: 50%; background: #e8f3d8; color: #438007; font-weight: 700; }
.itinerary-guide__case h4 { margin: 0 0 4px; font-size: 14px; }
.itinerary-guide__legend { display: grid; grid-template-columns: 18px auto 18px 1fr; align-items: center; gap: 7px 9px; margin-bottom: 15px; }
.itinerary-guide__swatch { width: 18px; height: 18px; border: 1px solid #dce2e7; border-radius: 4px; background: #f9fafb; }
.itinerary-guide__swatch--new { border-color: #b8d992; background: #f6fbef; }
.itinerary-guide__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 9px 16px; }
.itinerary-guide__actions div { display: flex; align-items: center; gap: 9px; }
.itinerary-guide__actions strong { min-width: 62px; color: #438007; font-size: 13px; }
.itinerary-guide__actions svg { flex: none; width: 20px; height: 20px; color: #438007; }
.itinerary-guide__actions .itinerary-guide__remove-icon { color: #a04c38; }
.itinerary-guide__operations { margin-top: 15px!important; padding: 11px 12px; border-radius: 6px; background: #f6f6f6; color: #555; }
@media (max-width: 600px) {
  .itinerary-guide__actions { grid-template-columns: 1fr; }
}
</style>
